import type { StoreApi } from 'zustand';
import type { AppState } from './store';
import { DEFAULT_SETTINGS, garmentRgb, knockoutTolerance, stepsForGarment } from './store';
import { PREVIEW_MAX, type EngineApi, type InspectLite, type PreviewResult, type Which } from '../worker/protocol';

let generation = 0;
export function nextGeneration(): number { return ++generation; }

export interface RunnerDeps {
  api: Pick<EngineApi, 'run' | 'preview' | 'inspect'>;
  onPreview(id: string, which: Which, p: PreviewResult): void;
  debounceMs?: number;
  /** Wrap callbacks for the transport (Comlink.proxy in the browser); identity in tests. */
  wrap?: <T extends (...a: never[]) => void>(fn: T) => T;
}

export function createRunner(store: StoreApi<AppState>, deps: RunnerDeps) {
  /** The debounced work pending per doc. `full` distinguishes a run from a re-inspect
   *  so the two can never be confused for each other (see `schedule`). */
  const timers = new Map<string, { timer: ReturnType<typeof setTimeout>; full: boolean }>();
  const wrap = deps.wrap ?? ((fn) => fn);
  /** The vendor preset each doc's most recently STARTED inspection was called with. */
  const inspectedPreset = new Map<string, string>();
  let disposed = false;

  /** A corrupt persisted garmentHex must not wedge a run: fall back once, up front. */
  function safeGarmentHex(): string {
    const hex = store.getState().settings.garmentHex;
    try { garmentRgb(hex); return hex; } catch { return DEFAULT_SETTINGS.garmentHex; }
  }

  function inspectSettings(): InspectLite {
    const st = store.getState();
    return {
      presetId: st.settings.presetId,
      garment: garmentRgb(safeGarmentHex()),
      // The near-garment-colour check has to use the same tolerance the knockout
      // step used, or it judges the output against a threshold nothing produced.
      garmentTolerance: knockoutTolerance(st.steps),
      targetWidthIn: st.settings.targetWidthIn ?? undefined,
    };
  }

  /**
   * Claim the next inspection of this doc's output and return its token. The
   * token is the doc's own `inspectGeneration`, kept separately from the run
   * generation: two inspections can overlap (a second preset change while the
   * first is still streaming) and would otherwise be indistinguishable, so both
   * would append their checks and the first to finish would clear `inspecting`.
   */
  function beginInspection(id: string, previewLanded = false): number {
    const gen = nextGeneration();
    // `previewLanded` folds the preview epoch into the same write. The viewer's
    // overlay cache key is `previewVersion:inspectVersion`, so bumping them
    // separately leaves an intermediate epoch that — if it ever rendered — would
    // refetch every enabled overlay for nothing, the exact shape review #7
    // removed. One write makes that structural rather than reliant on React
    // batching two adjacent `set` calls.
    store.getState().startInspection(id, gen, previewLanded);
    return gen;
  }

  /**
   * Re-inspect if the vendor preset moved on while the last inspection (or a run
   * that had not reached its inspect yet) was in flight.
   *
   * `inspectOnly` defers to work already in flight, which is only sound if
   * something afterwards reconciles the settings that work actually read: a
   * change arriving before `inspectSettings()` is read is picked up for free,
   * one arriving after it is not. This is that something, and it is also the
   * only path that recovers a deferred preset change from a run that REJECTED —
   * the catch writes no inspection at all, so the inspector would otherwise sit
   * on the previous preset's thresholds indefinitely.
   *
   * It converges: the pass it schedules records the current preset, so the next
   * reconcile is a no-op unless the user changed the preset again.
   */
  function reconcilePreset(id: string) {
    const st = store.getState();
    const doc = st.docs.find((d) => d.id === id);
    if (!doc) { inspectedPreset.delete(id); return; }      // closed: drop its bookkeeping
    // Both guards matter. No `run` means no output to inspect, and no recorded
    // preset means no inspection has ever read one for this doc — in either case
    // `inspectNow` would fall through to a full RUN, which on the rejection path
    // would silently auto-retry a failed run and clear its `runError`.
    if (!doc.run || !inspectedPreset.has(id)) return;
    if (inspectedPreset.get(id) !== st.settings.presetId) schedule(id, false, () => void inspectNow(id));
  }

  /** Run the inspection claimed by `gen`, streaming checks in as they land. */
  async function inspectInto(id: string, gen: number) {
    const isCurrent = () => store.getState().docs.find((d) => d.id === id)?.inspectGeneration === gen;
    const settings = inspectSettings();
    inspectedPreset.set(id, settings.presetId);
    const checks = await deps.api.inspect(id, 'output', settings, gen, wrap((c) => {
      const d = store.getState().docs.find((x) => x.id === id);
      if (d && d.inspectGeneration === gen) store.getState().setDocStatus(id, { checks: [...d.checks, c] });
    }));
    if (disposed) return;
    // `inspecting` only flips false for the inspection that is still the current one.
    if (!isCurrent()) return;      // a newer inspection owns the doc; it reconciles for us
    store.getState().setDocStatus(id, { checks, inspecting: false });
    reconcilePreset(id);
  }

  async function runNow(id: string) {
    const st = store.getState();
    const doc = st.docs.find((d) => d.id === id);
    if (!doc || doc.status !== 'ready') return;
    const gen = nextGeneration();
    const steps = stepsForGarment(st.steps, safeGarmentHex());
    store.getState().setDocStatus(id, { running: true, runningGeneration: gen });

    try {
      // No onStep: the runner does not use per-step progress, and proxying a
      // no-op would cost a MessageChannel per run (see EngineApi.run).
      const result = await deps.api.run(id, steps, doc.meta?.dpi ?? st.settings.dpi, gen);
      if (disposed) return;
      if (!store.getState().docs.some((d) => d.id === id)) return;
      if (result.cancelled) {
        // a newer run may already be spinning; only clear the flag if this
        // generation is still the one the spinner is tracking
        const c = store.getState().docs.find((d) => d.id === id);
        if (c?.runningGeneration === gen) store.getState().setDocStatus(id, { running: false });
        return;
      }
      // a newer generation may have finished first; keep only the newest
      const current = store.getState().docs.find((d) => d.id === id);
      if (current?.run && current.run.generation > result.generation) return;
      store.getState().setDocStatus(id, {
        run: result,
        checks: [],
        inspecting: true,
        runError: undefined,
        ...(current?.runningGeneration === gen ? { running: false } : {}),
      });
      const preview = await deps.api.preview(id, 'output', PREVIEW_MAX);
      if (disposed) return;
      if (!store.getState().docs.some((d) => d.id === id)) { preview?.bitmap.close(); return; }
      if (preview) deps.onPreview(id, 'output', preview);
      await inspectInto(id, beginInspection(id, !!preview));
    } catch (err) {
      if (disposed) return;
      const current = store.getState().docs.find((d) => d.id === id);
      if (!current) return;
      // a newer run is in flight or has already finished (success or failure): a stale
      // failure must never clobber it, and must never touch `status` (that field is
      // reserved for open/decode failures, so a transient run error can't wedge the doc)
      if (current.runningGeneration !== gen) return;
      if (current.run && current.run.generation > gen) return;
      store.getState().setDocStatus(id, { runError: (err as Error).message, running: false, inspecting: false });
      // This run wrote no inspection, so a preset change that deferred to it
      // would be lost: re-arm it against whatever output is still there.
      reconcilePreset(id);
    }
  }

  /**
   * Re-inspect the output already in the worker, without re-running the
   * pipeline. Used for changes that only the inspector reads (the vendor
   * preset): a full run on a 5225² file costs ~5 s and would produce
   * byte-identical pixels.
   */
  async function inspectNow(id: string) {
    const doc = store.getState().docs.find((d) => d.id === id);
    if (!doc || doc.status !== 'ready') return;
    // Nothing has been produced yet (the first run is still pending or in
    // flight), so there is no output to inspect: run instead.
    if (!doc.run) { await runNow(id); return; }
    const gen = beginInspection(id);
    try {
      await inspectInto(id, gen);
    } catch (err) {
      if (disposed) return;
      const current = store.getState().docs.find((d) => d.id === id);
      // a newer inspection (or a run's own) owns the checks now: stay quiet
      if (!current || current.inspectGeneration !== gen) return;
      store.getState().setDocStatus(id, { runError: (err as Error).message, inspecting: false });
      reconcilePreset(id);
    }
  }

  /**
   * One debounce slot per doc, but the two kinds of work are not
   * interchangeable. A full run supersedes a pending re-inspect (it inspects
   * when it lands, reading the settings current at that moment). The reverse
   * would LOSE work: a preset change arriving inside a width commit's debounce
   * window used to replace the pending run with a re-inspect of the old output,
   * leaving the settings bar, the resize card and the inspector all saying the
   * new width while the preview and `exportPng` still held the old one.
   */
  function schedule(id: string, full: boolean, work: () => void) {
    const pending = timers.get(id);
    if (pending) {
      if (!full && pending.full) return;        // never downgrade a pending run to a re-inspect
      clearTimeout(pending.timer);
    }
    timers.set(id, { timer: setTimeout(() => { timers.delete(id); work(); }, deps.debounceMs ?? 250), full });
  }

  return {
    trigger(id: string) { schedule(id, true, () => void runNow(id)); },
    /**
     * Re-inspect only, for a change the pipeline does not read (the vendor
     * preset). Falls back to a full run if the document has no output yet, and
     * defers to a run OR an inspection already in flight rather than restarting
     * a nine-check pass that is already producing the right answer. `inspecting`
     * is in the guard as well as `running` because a run clears `running` when
     * its result lands, ~5 s before its own inspection finishes on a large file,
     * so without it a preset change in that window emptied and re-streamed the
     * inspector for the same preset. Either way `reconcilePreset` re-arms the
     * change afterwards if the deferred-to work read an older preset.
     */
    inspectOnly(id: string) {
      const doc = store.getState().docs.find((d) => d.id === id);
      if (doc?.running || doc?.inspecting) return;
      schedule(id, false, () => void inspectNow(id));
    },
    dispose() { disposed = true; for (const t of timers.values()) clearTimeout(t.timer); timers.clear(); inspectedPreset.clear(); },
  };
}
